import axios from 'axios';
import InputField from 'components/fields/InputField';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MdArrowForward } from 'react-icons/md';
import { ToastContainer, toast } from 'react-toastify';
import { CONFIG } from 'variables/config';

const MONTH_NAMES = [
 '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const UpdateSellerInvoiceInformation = () => {
 const [user, setUser] = useState(null);
 const [defaultUser, setDefaultUser] = useState(null);
 const [invoices, setInvoices] = useState([]);
 const [loadingInvoices, setLoadingInvoices] = useState(false);
 const [ibanJustSaved, setIbanJustSaved] = useState(false);
 const nav = useNavigate();
 const [searchParams] = useSearchParams();
 // Seller was sent here from the cagnotte page to add their IBAN before
 // requesting a withdrawal — show a way back once it's saved.
 const fromCagnotte = searchParams.get('from') === 'cagnotte';

 const initUser = () => {
 const token = localStorage.getItem('token');
 const config = {
 headers: { Authorization: `Bearer ${token}` },
 };
 axios
 .get(`${CONFIG.apiUrl}/user/me`, config)
 .then((res) => {
 const user = res.data;
 setUser(user);
 setDefaultUser(user);
 })
 .catch((err) => {
 console.log(err);
 });
 };

 const fetchInvoices = () => {
 const token = localStorage.getItem('token');
 const config = {
 headers: { Authorization: `Bearer ${token}` },
 };
 setLoadingInvoices(true);
 axios
 .get(`${CONFIG.apiUrl}/seller/invoices`, config)
 .then((res) => {
 setInvoices(res.data.invoices || []);
 setLoadingInvoices(false);
 })
 .catch((err) => {
 console.log(err);
 setLoadingInvoices(false);
 });
 };

 const handleChange = (e: any) => {
 const { id, value } = e.target;
 setUser({ ...user, [id]: value });
 };

 const updateProfil = () => {
 const token = localStorage.getItem('token');
 const config = {
 headers: { Authorization: `Bearer ${token}` },
 };
 const data = {
 company: user.company,
 address: user.address,
 vatNumber: user.vatNumber,
 siren: user.siren,
 iban: user.iban,
 bic: user.bic,
 };
 axios
 .put(`${CONFIG.apiUrl}/user`, data, config)
 .then((res) => {
 toast.success('Votre profil a été mis à jour');
 if (fromCagnotte && data.iban) setIbanJustSaved(true);
 })
 .catch((err) => {
 console.log(err);
 });
 };

 const downloadInvoice = (year: number, month: number) => {
 const token = localStorage.getItem('token');
 window.open(
 `${CONFIG.apiUrl}/seller/invoice/${year}/${month}?token=${encodeURI(token)}`,
 '_blank'
 );
 };

 const areInvoiceInfoValid = () => {
 return user?.siren && user?.company && user?.address;
 };

 useEffect(() => {
 initUser();
 fetchInvoices();
 }, []);

 return (
 <>
 <div className="rounded-xl border border-leadrs-stroke bg-white px-4 py-4">
 <ToastContainer />
 {/* Header */}
 <div className="w-full px-1">
 <h4 className="text-base font-bold text-leadrs-text">
 Information de facturation
 </h4>
 </div>

 {fromCagnotte && (
 ibanJustSaved ? (
 <div className="mt-3 flex flex-col gap-3 rounded-lg bg-leadrs-green-soft px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
 <p className="font-geist text-sm leading-relaxed text-leadrs-green-focus">
 Votre IBAN est enregistré. Vous pouvez maintenant demander votre retrait.
 </p>
 <button
 onClick={() => nav('/cagnotte')}
 className="inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-lg bg-leadrs-brand px-5 py-2.5 font-geist text-sm font-medium text-white transition-colors hover:bg-leadrs-brand-light"
 >
 Retourner à ma cagnotte
 <MdArrowForward className="h-4 w-4" />
 </button>
 </div>
 ) : (
 <div className="mt-3 flex flex-col gap-2 rounded-lg bg-leadrs-brown-soft px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
 <p className="font-geist text-sm leading-relaxed text-leadrs-brown-focus">
 Renseignez votre IBAN ci-dessous, puis enregistrez pour pouvoir demander un retrait de votre cagnotte.
 </p>
 <button
 onClick={() => nav('/cagnotte')}
 className="flex-shrink-0 font-geist text-sm font-medium text-leadrs-brown-focus underline transition-opacity hover:opacity-70"
 >
 Retour à ma cagnotte
 </button>
 </div>
 )
 )}

 {/* inputs */}
 <div key={user?._id || 'loading'} className="mt-4 grid grid-cols-2 gap-2">
 <InputField
 extra="mb-2"
 label="Mon entreprise"
 placeholder="Mon entreprise"
 defaultValue={user?.company}
 id="company"
 type="text"
 onChange={handleChange}
 />
 <InputField
 extra="mb-2"
 label="Adresse"
 placeholder="17 rue de la paix, 75000 Paris France"
 defaultValue={user?.address}
 id="address"
 type="text"
 onChange={handleChange}
 />
 <InputField
 extra="mb-2"
 label="Numéro de TVA"
 placeholder="FR12345678901"
 defaultValue={user?.vatNumber}
 id="vatNumber"
 type="text"
 onChange={handleChange}
 />
 <InputField
 extra="mb-2"
 label="Numéro de SIREN"
 placeholder="123456789"
 defaultValue={user?.siren}
 id="siren"
 type="text"
 onChange={handleChange}
 />
 <InputField
 extra="mb-2"
 label="IBAN"
 placeholder="FR76 1234 5678 9012 3456 7890 123"
 defaultValue={user?.iban}
 id="iban"
 type="text"
 onChange={handleChange}
 />
 <InputField
 extra="mb-2"
 label="BIC"
 placeholder="BNPAFRPPXXX"
 defaultValue={user?.bic}
 id="bic"
 type="text"
 onChange={handleChange}
 />
 </div>
 <div className="mt-3 flex w-full justify-end">
 <button
 className="linear rounded-lg bg-leadrs-brand px-4 py-1.5 text-sm font-medium text-white transition duration-200 hover:cursor-pointer"
 onClick={() => updateProfil()}
 >
 Sauvegarder
 </button>
 </div>
 </div>

 {/* Monthly Invoices List */}
 <div className="mt-4 rounded-xl border border-leadrs-stroke bg-white px-4 py-4">
 <div className="w-full px-1">
 <div className="flex items-center justify-between">
 <h4 className="text-base font-bold text-leadrs-text">
 Mes factures
 </h4>
 <button
 className="linear rounded-lg bg-leadrs-brand px-3 py-1.5 text-xs font-medium text-white transition duration-200 hover:cursor-pointer"
 onClick={() => fetchInvoices()}
 disabled={loadingInvoices}
 >
 {loadingInvoices ? 'Chargement...' : 'Actualiser'}
 </button>
 </div>
 </div>

 <div className="mt-4">
 {!areInvoiceInfoValid() && (
 <div className="mb-3 rounded-lg bg-leadrs-yellow-soft px-3 py-2 text-sm text-leadrs-yellow-focus">
 Veuillez compléter vos informations de facturation ci-dessus pour pouvoir télécharger vos factures.
 </div>
 )}
 {loadingInvoices ? (
 <div className="flex justify-center py-4">
 <p className="text-sm text-leadrs-text-soft">Chargement des factures...</p>
 </div>
 ) : invoices.length === 0 ? (
 <div className="flex justify-center py-4">
 <p className="text-sm text-leadrs-text-soft">Aucune facture disponible</p>
 </div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead>
 <tr className="border-b border-leadrs-stroke">
 <th className="px-3 py-2 text-left text-xs font-semibold text-leadrs-text">
 Période
 </th>
 <th className="px-3 py-2 text-left text-xs font-semibold text-leadrs-text">
 Nombre de leads
 </th>
 <th className="px-3 py-2 text-left text-xs font-semibold text-leadrs-text">
 Montant HT
 </th>
 <th className="px-3 py-2 text-left text-xs font-semibold text-leadrs-text">
 Actions
 </th>
 </tr>
 </thead>
 <tbody>
 {invoices.map((invoice: any) => (
 <tr
 key={`${invoice.year}-${invoice.month}`}
 className="border-b border-leadrs-stroke hover:bg-leadrs-bg"
 >
 <td className="px-3 py-2 text-xs text-leadrs-text">
 {MONTH_NAMES[invoice.month]} {invoice.year}
 </td>
 <td className="px-3 py-2 text-xs text-leadrs-text-soft">
 {invoice.totalLeads}
 </td>
 <td className="px-3 py-2 text-xs font-medium text-leadrs-text">
 {invoice.totalAmountHT.toFixed(2)} EUR
 </td>
 <td className="px-3 py-2 text-xs">
 <button
 onClick={() => downloadInvoice(invoice.year, invoice.month)}
 disabled={!areInvoiceInfoValid()}
 className={`underline ${
 areInvoiceInfoValid()
 ? 'text-leadrs-brand'
 : 'cursor-not-allowed text-gray-400'
 }`}
 >
 Télécharger PDF
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 </div>
 </>
 );
};

export default UpdateSellerInvoiceInformation;
